import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Calendar, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const leaveTypes = [
  'Full Day',
  'Half Day',
  'WFH (PAID)',
  'WFH (Half Day)',
  'WFH (Unpaid)',
  'Unpaid',
  'Comp Off',
];

export default function ApplyLeave({ user, onLogout }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    leave_reason: '',
    start_date: '',
    end_date: '',
    leave_type: 'Full Day',
  });
  const [loading, setLoading] = useState(false);
  const [balanceWarning, setBalanceWarning] = useState(null);
  const [leaveCalculation, setLeaveCalculation] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);

  useEffect(() => {
    fetchBalanceWarning();
  }, []);

  const fetchBalanceWarning = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/me/balance-warning`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalanceWarning(response.data);
    } catch (error) {
      console.error('Failed to fetch balance warning');
    }
  };

  // Fetch leave calculation from backend (excludes weekends and holidays)
  const fetchLeaveCalculation = useCallback(async () => {
    if (!formData.start_date || !formData.end_date) {
      setLeaveCalculation(null);
      return;
    }
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    if (end < start) {
      setLeaveCalculation(null);
      return;
    }

    setCalculationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaves/calculate-days`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: formData.start_date,
          end_date: formData.end_date,
          leave_type: formData.leave_type,
        },
      });
      setLeaveCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate leave days');
      setLeaveCalculation(null);
    } finally {
      setCalculationLoading(false);
    }
  }, [formData.start_date, formData.end_date, formData.leave_type]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchLeaveCalculation();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchLeaveCalculation]);

  // Calculate actual leave days (adjusted for half days)
  const getActualLeaveDays = () => {
    if (!leaveCalculation) return null;
    const workingDays = leaveCalculation.working_days;
    if (formData.leave_type === 'Half Day' || formData.leave_type === 'WFH (Half Day)') {
      return workingDays * 0.5;
    }
    return workingDays;
  };

  const leaveDays = getActualLeaveDays();

  const setQuickDate = (type) => {
    const today = new Date();
    const formatted = today.toISOString().split('T')[0];
    
    if (type === 'today') {
      setFormData({ ...formData, start_date: formatted, end_date: formatted });
    } else if (type === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowFormatted = tomorrow.toISOString().split('T')[0];
      setFormData({ ...formData, start_date: tomorrowFormatted, end_date: tomorrowFormatted });
    } else if (type === 'this-week') {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (5 - today.getDay()));
      setFormData({ ...formData, start_date: formatted, end_date: endOfWeek.toISOString().split('T')[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leaves`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Leave request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Apply for Leave</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">Submit a new leave request</p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-8 max-w-3xl">
          {/* Balance Warnings */}
          {balanceWarning && balanceWarning.warnings.length > 0 && (
            <div className="mb-4 space-y-2">
              {balanceWarning.warnings.map((warning, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border flex items-start gap-2 ${
                    warning.type === 'zero_balance' 
                      ? 'bg-red-50 border-red-200 text-red-800' 
                      : warning.type === 'half_day_exceed'
                      ? 'bg-orange-50 border-orange-200 text-orange-800'
                      : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{warning.message}</p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl border border-border p-4 md:p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Leave Reason
                </label>
                <textarea
                  data-testid="leave-reason-input"
                  required
                  value={formData.leave_reason}
                  onChange={(e) => setFormData({ ...formData, leave_reason: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px] md:min-h-[100px]"
                  placeholder="Describe the reason for your leave request..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Quick Presets
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setQuickDate('today')}
                    data-testid="preset-today"
                    className="px-3 md:px-4 py-2 text-xs md:text-sm font-medium bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDate('tomorrow')}
                    data-testid="preset-tomorrow"
                    className="px-4 py-2 text-sm font-medium bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors"
                  >
                    Tomorrow
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    data-testid="start-date-input"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    data-testid="end-date-input"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Leave Type
                </label>
                <select
                  data-testid="leave-type-select"
                  required
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {leaveTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                
                {/* Leave Days Calculation */}
                {calculationLoading && (
                  <div className="mt-3 p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Calculating leave days...</p>
                  </div>
                )}
                
                {!calculationLoading && leaveCalculation && leaveDays !== null && (
                  <div className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/20" data-testid="leave-days-info">
                    <p className="text-sm font-medium text-primary">
                      📅 {leaveDays === 1 ? '1 Day' : `${leaveDays} Days`} Leave
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      From {new Date(formData.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to {new Date(formData.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    
                    {/* Show excluded days info */}
                    {(leaveCalculation.excluded_weekends.length > 0 || leaveCalculation.excluded_holidays.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-primary/20">
                        <div className="flex items-start gap-1">
                          <Info className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-muted-foreground">
                            {leaveCalculation.excluded_weekends.length > 0 && (
                              <p>
                                <span className="font-medium">Weekends excluded:</span>{' '}
                                {leaveCalculation.excluded_weekends.map(d => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })).join(', ')}
                              </p>
                            )}
                            {leaveCalculation.excluded_holidays.length > 0 && (
                              <p className="mt-1">
                                <span className="font-medium">Holidays excluded:</span>{' '}
                                {leaveCalculation.excluded_holidays.map(d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })).join(', ')}
                              </p>
                            )}
                            <p className="mt-1 text-primary/70">
                              Total calendar days: {leaveCalculation.total_calendar_days} → Working days: {leaveCalculation.working_days}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  data-testid="submit-leave-button"
                  disabled={loading}
                  className="flex-1 bg-primary text-white hover:bg-primary/90 shadow-sm transition-all active:scale-95 rounded-lg px-6 py-3 font-heading font-semibold disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  data-testid="cancel-button"
                  className="bg-white text-foreground border border-input hover:bg-muted shadow-sm rounded-lg px-6 py-3 font-heading font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}